import { useEffect, useRef, useState } from 'react'
import { ImagePlus, Loader2 } from 'lucide-react'
import type { BreakdownPhoto } from '../lib/types'
import { uploadBreakdownPhoto } from '../lib/breakdownApi'

/** Evidence for one incident. Upload goes straight to S3 via a presigned URL this service
 *  mints -- the bytes never pass through the API -- then this component records the result
 *  against breakdownId so the incident's photo history survives the vehicle's next one. */
export function BreakdownPhotos({
  vehicleId, breakdownId,
}: { vehicleId: string; breakdownId: number }): JSX.Element {
  const [photos, setPhotos] = useState<BreakdownPhoto[]>([])
  const [loaded, setLoaded] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [provisioned, setProvisioned] = useState(true)
  const fileInput = useRef<HTMLInputElement>(null)

  const load = async (): Promise<void> => {
    try {
      const res = await fetch(
        `/api/vehicle/${encodeURIComponent(vehicleId)}/breakdown/${breakdownId}/photos`)
      const body = await res.json() as { photos?: BreakdownPhoto[] }
      setPhotos(body.photos ?? [])
    } catch {
      // A photo gallery that cannot load is not worth interrupting the form over.
    } finally {
      setLoaded(true)
    }
  }

  useEffect(() => { void load() }, [vehicleId, breakdownId])

  async function onFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return
    setUploading(true)
    setError(null)
    try {
      for (const file of Array.from(files)) {
        await uploadBreakdownPhoto(vehicleId, breakdownId, file)
      }
      await load()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'upload failed'
      if (/not provisioned/i.test(msg)) setProvisioned(false)
      setError(msg)
    } finally {
      setUploading(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  if (!provisioned) {
    return (
      <p className="text-[11px] text-[#8794A7]">
        Photo storage is not set up for this environment yet.
      </p>
    )
  }

  return (
    <div className="grid gap-2">
      {loaded && photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {photos.map((p) => (
            <a key={p.photo_id} href={p.url ?? '#'} target="_blank" rel="noreferrer"
               className="block h-14 w-14 overflow-hidden rounded-lg border border-[rgba(15,23,42,0.10)] bg-[rgba(15,23,42,0.04)]">
              {p.url ? (
                <img src={p.url} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="grid h-full w-full place-items-center text-[10px] text-[#8794A7]">···</span>
              )}
            </a>
          ))}
        </div>
      )}
      <label className="flex w-fit cursor-pointer items-center gap-1.5 rounded-lg border border-[rgba(15,23,42,0.12)]
                        px-2.5 py-1.5 text-[11.5px] text-[#475569] hover:bg-[rgba(15,23,42,0.04)]">
        {uploading ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
        {uploading ? 'Uploading…' : 'Add photo'}
        <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp,image/heic"
               multiple className="hidden" onChange={(e) => void onFiles(e.target.files)} />
      </label>
      {error && <p className="text-[11px] text-[#9F1239]">{error}</p>}
    </div>
  )
}
